/**
 * Universal Control: which products it applies to, and where its mobile app lives. Shared by
 * web-fender's installer buttons and ns-myfender's product page so the two offer it on the same
 * products (DENG-1343).
 *
 * The desktop app arrives through the product snapshot's `softwareDownloads`. Its mobile app
 * doesn't: PIM carries no store links for it (Quantum HD has an Android `.apk`, which isn't
 * something to hand a customer), so the store links live here.
 */

/**
 * The `softwareDownloads` group type paeapi gives Universal Control when a hardware product
 * bundles it — `SOFTWARE_FAMILIES` in paeapi's `src/helpers/product.ts`. Quantum HD and the
 * StudioLive mixers carry it, for example; Tone Master Pro doesn't (its app is Pro Control).
 */
export const UC_SOFTWARE_FAMILY = "Universal Control";

/**
 * The mobile app. iOS is iPad-only, which is why it's offered as "for iPad & Android". Google
 * Play still lists it under PreSonus's original package name.
 */
export const UC_MOBILE_APP = {
  title: "Fender Universal Control",
  appStoreUrl: "https://apps.apple.com/app/id6755309706",
  playStoreUrl: "https://play.google.com/store/apps/details?id=com.presonus.ucremote",
};

interface ProductLike {
  title?: string;
  stringId?: string;
  categoryId?: string;
}

interface SnapshotLike {
  product?: ProductLike;
  softwareDownloads?: Array<{ type?: string }>;
}

/**
 * True for the Universal Control product itself ("Fender Universal Control v5" today, string id
 * `ucapp5`). Matched loosely, since the title and string id both change with each major version.
 */
export function isUniversalControlProduct(product: ProductLike | null | undefined): boolean {
  if (!product) return false;
  if (`${product.stringId || ""}`.toLowerCase().startsWith("ucapp")) return true;
  return product.categoryId === "software" && /universal control/i.test(product.title || "");
}

/** True when a `softwareDownloads` group is Universal Control: bundled with hardware, or the UC product's own group. */
export function isUniversalControlGroup(groupType: string | null | undefined, product: ProductLike | null | undefined): boolean {
  if (groupType === UC_SOFTWARE_FAMILY) return true;
  return isUniversalControlProduct(product) && !!groupType && groupType === product?.title;
}

/** True for the UC product and for any product its snapshot says works with UC. */
export function supportsUniversalControl(snapshot: SnapshotLike | null | undefined): boolean {
  if (!snapshot) return false;
  if (isUniversalControlProduct(snapshot.product)) return true;
  return (snapshot.softwareDownloads || []).some((group) => group?.type === UC_SOFTWARE_FAMILY);
}
